import { useQuery } from "@tanstack/react-query";
import { EntityTypeLifecyclePanel } from "@/features/entity-types/components/entity-type-lifecycle-panel";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import type { StatusLifecycleDisplay } from "@/services/lifecycle/lifecycle.types";
import { isSystemLifecycle } from "@/utils/lifecycle-detail/lifecycle-detail.utils";
import { useAssetTypeDrawerContext } from "./asset-type-drawer-context";

interface AssetTypeLifecycleSectionProps {
	organizationId: string;
	variant?: "drawer" | "full";
}

export function AssetTypeLifecycleSection({
	organizationId,
}: AssetTypeLifecycleSectionProps) {
	const { t } = useAppTranslation();
	const { assetType } = useAssetTypeDrawerContext();
	const lifecycleId = assetType.statusLifecycle?.id ?? null;
	const lifecycleName = assetType.statusLifecycle?.name?.trim() || "";
	const hasLifecycle = !!lifecycleId;

	const lifecycleDetailQuery = useQuery({
		...lifecycleQueryOptions.statusLifecycleDetail(
			lifecycleId ?? "",
			organizationId,
		),
		enabled: !!lifecycleId,
	});

	const lifecycle = (lifecycleDetailQuery.data ??
		null) as StatusLifecycleDisplay | null;

	return (
		<EntityTypeLifecyclePanel
			name={lifecycle?.name ?? lifecycleName}
			description={lifecycle?.description}
			lifecycle={lifecycle}
			hasLifecycle={hasLifecycle}
			isSystem={lifecycle ? isSystemLifecycle(lifecycle) : null}
			isLoading={hasLifecycle && lifecycleDetailQuery.isPending}
			emptyTitle={t("admin.assetTypes.drawer.lifecycle.noLifecycle")}
			emptyDescription={t(
				"admin.assetTypes.drawer.lifecycle.noLifecycleSubtext",
			)}
			errorMessage={t("admin.assetTypes.drawer.lifecycle.visualizationError")}
		/>
	);
}
